import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Scale, BriefcaseBusiness, Shield,
  Hash, Scan, Fingerprint, Layers, Brain, Clock, BadgeCheck,
  ChevronRight, Zap, Lock, Globe, Eye, GitCompareArrows,
  AlertTriangle, CheckCircle2, ShieldCheck, Cpu,
  ScanSearch, Workflow, Radio, Sun, Camera,
} from "lucide-react";
import { ScanEyeLogoPrueba } from "../components/ScanEyeLogoPrueba";

const FEATURES = [
  { icon: Hash,             label: "SHA-256 · MD5 · SHA-1",         desc: "Huella criptográfica triple conforme ISO 27037" },
  { icon: Scan,             label: "Error Level Analysis",           desc: "Detección de manipulación por compresión JPEG diferencial" },
  { icon: Fingerprint,      label: "EXIF + XMP profundo",            desc: "Historial de software, GPS y coherencia de hardware" },
  { icon: Brain,            label: "IA Gemini Vision",               desc: "Análisis multimodal con 9 vectores forenses" },
  { icon: Clock,            label: "TSA RFC 3161",                   desc: "Sello de tiempo certificado FreeTSA.org verificable" },
  { icon: Layers,           label: "C2PA ISO 21617",                 desc: "Detección de Content Credentials en imágenes" },
  { icon: GitCompareArrows, label: "ORB Copy-Move",                  desc: "Detección de clones rotados, espejados y escalados con ORB" },
  { icon: Eye,              label: "Inpainting generativo",          desc: "Detecta relleno IA (Stable Diffusion, Adobe Gen Fill) por análisis de ruido local" },
  { icon: Globe,            label: "Geocodificación inversa",        desc: "Validación cruzada GPS ↔ metadatos ↔ timestamp" },
  { icon: Lock,             label: "Cadena de custodia",             desc: "Referencia inmutable SYE-YYYYMMDD para uso judicial" },
  { icon: Cpu,              label: "PRNU + JPEG Ghost",              desc: "Patrón de ruido de sensor y análisis de historial de compresión" },
  { icon: ShieldCheck,      label: "Sellado TSA + firma digital",    desc: "Informe PDF con sello de tiempo certificado e integridad verificable" },
  { icon: ScanSearch,       label: "LSB Steganography",             desc: "Detección de datos ocultos en el plano de bits menos significativo" },
  { icon: Camera,           label: "Identificación cámara (QT)",    desc: "Identifica fabricante y software por tabla de cuantización JPEG" },
  { icon: Layers,           label: "Alineación bloque JPEG",        desc: "Detecta splicing por desalineación de rejilla DCT 8×8" },
  { icon: Radio,            label: "ENF — Red Eléctrica",          desc: "Análisis de frecuencia de red eléctrica (50/60 Hz) en audio para detectar edición" },
  { icon: Sun,              label: "Verificación Solar GPS+Hora",   desc: "Calcula posición solar (Meeus/NOAA) y verifica coherencia con iluminación EXIF" },
];

const LEGAL_SIGNALS = [
  { label: "ELA (Error Level Analysis)",       weight: "Fuerte",   note: "Mapa diferencial de compresión JPEG" },
  { label: "PRNU — Ruido de sensor",           weight: "Fuerte",   note: "Coherencia interna del patrón de ruido" },
  { label: "JPEG Ghost — Historial compresión",weight: "Fuerte",   note: "Recompresiones ocultas en capas DCT" },
  { label: "EXIF + XMP + timestamps",          weight: "Medio",    note: "Cadena de metadatos completa" },
  { label: "Copy-Move dHash (exacto)",         weight: "Duro",     note: "Bloques clonados pixel a pixel" },
  { label: "ORB Copy-Move (rotacional)",       weight: "Duro",     note: "Clones rotados, espejados y escalados" },
  { label: "Inpainting generativo",            weight: "Duro",     note: "Relleno IA por varianza de ruido local" },
  { label: "Thumbnail vs. imagen actual",      weight: "Duro",     note: "Miniatura diferente = archivo editado" },
  { label: "Timestamps imposibles",            weight: "Duro",     note: "Fechas futuras, época Unix, discrepancias" },
  { label: "LSB Steganography",               weight: "Medio",    note: "Chi² + entropía LSB — datos ocultos en píxeles" },
  { label: "Alineación bloque JPEG (CFA)",    weight: "Medio",    note: "Desalineación rejilla DCT indica splicing" },
  { label: "Verificación solar GPS+hora",     weight: "Medio",    note: "Posición solar NOAA vs iluminación de imagen" },
];

const LEGAL_EXCLUDED = [
  { label: "Gemini Vision AI score",    reason: "Modelo actualizable sin aviso — resultado puede cambiar con la misma imagen" },
  { label: "Sightengine deepfake score",reason: "API externa de terceros — no reproducible en un juicio meses después" },
];

const VERIFY_SIGNALS = [
  { label: "Gemini Vision — 9 vectores",        type: "IA",      note: "Semántica, deepfake, GAN fingerprint, luz..." },
  { label: "Sightengine deepfake API",           type: "IA",      note: "Modelo ML de detección facial anti-deepfake" },
  { label: "ELA + PRNU + JPEG Ghost",            type: "Técnico", note: "Batería forense completa" },
  { label: "ORB Copy-Move rotacional",           type: "Técnico", note: "Clones geométricamente transformados" },
  { label: "Inpainting generativo (SD/DALL·E)",  type: "Técnico", note: "Relleno IA por análisis de ruido local" },
  { label: "Face deepfake ROI",                  type: "IA",      note: "Análisis por región facial" },
  { label: "GPS + timestamp coherence",          type: "Técnico", note: "Cruce de metadatos de ubicación y hora" },
  { label: "LSB Steganography",                 type: "Técnico", note: "Detección de datos ocultos en píxeles" },
  { label: "Verificación solar GPS+hora",       type: "Técnico", note: "Posición solar NOAA vs iluminación EXIF" },
  { label: "Score de riesgo de fraude",          type: "Score",   note: "Ponderación multi-señal con confianza" },
];

const typeColor = { "IA": "#818CF8", "Técnico": "#DEFF9A", "Duro": "#FF7A7A", "Score": "#9AD7FF", "Fuerte": "#DEFF9A", "Medio": "#9AD7FF" };

export default function Onboarding() {
  const [activeEngine, setActiveEngine] = useState("legal");

  return (
    <div className="min-h-screen bg-[#060608] text-white overflow-x-hidden">

      {/* ── Background ── */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full bg-[#818CF8]/[0.035] blur-[140px]" />
        <div className="absolute bottom-1/3 right-0 w-[700px] h-[500px] rounded-full bg-[#DEFF9A]/[0.025] blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[400px] rounded-full bg-[#818CF8]/[0.02] blur-[100px]" />
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.018) 1px,transparent 1px)", backgroundSize: "72px 72px" }} />
      </div>

      {/* ── NAV ── */}
      <header className="relative z-20 border-b border-white/[0.06] bg-[#060608]/80 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Link to="/login" className="px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-white/50 hover:text-white border border-white/10 hover:border-white/25 rounded-md transition-all duration-200">
              Login
            </Link>
            <Link to="/register" className="px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-black bg-[#DEFF9A] hover:bg-[#C8FF6E] rounded-md transition-all duration-200 shadow-[0_0_18px_rgba(222,255,154,0.28)]">
              Registro
            </Link>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2">
            <ScanEyeLogoPrueba size="sm" />
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-white/25">
            <span className="w-1.5 h-1.5 rounded-full bg-[#DEFF9A] animate-pulse" />
            Forensic Engine v4.0
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 rounded-full border border-[#818CF8]/25 bg-[#818CF8]/[0.07] text-[10px] font-mono uppercase tracking-[0.22em] text-[#818CF8]">
            <Zap size={11} /> Nuevo: LSB Stego · Solar GPS · ENF Audio · Identificación Cámara QT
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[82px] tracking-[-0.04em] leading-[0.88] mb-6">
            Inteligencia forense
            <br />
            <span className="gradient-text" style={{ backgroundImage: "linear-gradient(135deg, #818CF8 0%, #a78bfa 40%, #DEFF9A 100%)" }}>
              para evidencia digital
            </span>
          </h1>

          <p className="text-base sm:text-lg text-white/50 leading-relaxed max-w-2xl mx-auto mb-10">
            ScanEye analiza imágenes, vídeo, audio y documentos con 17 técnicas forenses
            certificadas — SHA-256 triple, ELA, EXIF, C2PA, TSA RFC&nbsp;3161, ORB Copy-Move,
            ENF Audio, Stego LSB y verificación solar GPS — para uso legal o verificación profesional.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14">
            <Link to="/register" className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-white hover:bg-white/90 text-black font-semibold rounded-lg transition-all duration-200 shadow-[0_0_28px_rgba(255,255,255,0.12)] hover:shadow-[0_0_40px_rgba(255,255,255,0.2)]">
              Comenzar gratis <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 border border-white/15 hover:border-white/30 text-white/70 hover:text-white rounded-lg transition-all duration-200">
              Iniciar sesión
            </Link>
          </div>

          {/* Tech badges */}
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {[
              { v: "SHA-256", l: "Integridad" }, { v: "ELA", l: "Análisis error" },
              { v: "TSA", l: "RFC 3161" }, { v: "ORB", l: "Copy-Move" },
              { v: "Inpainting", l: "Detección IA" }, { v: "C2PA", l: "ISO 21617" },
            ].map(({ v, l }) => (
              <div key={v} className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] text-[11px] font-mono">
                <BadgeCheck size={11} className="text-[#DEFF9A]" />
                <span className="text-white font-semibold">{v}</span>
                <span className="text-white/30">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO TRABAJAN LOS MOTORES ── */}
      <section className="relative z-10 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center mb-14">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/25 mb-3">Arquitectura forense</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">Cómo trabaja cada motor</h2>
            <p className="text-white/40 text-sm max-w-xl mx-auto leading-relaxed">
              Dos pipelines completamente distintos. El motor que uses determina qué señales influyen en el veredicto y qué aparece solo como contexto explicativo.
            </p>
          </div>

          {/* Engine toggle */}
          <div className="flex justify-center mb-12">
            <div className="flex gap-1 p-1 rounded-xl border border-white/[0.08] bg-white/[0.03]">
              <button
                onClick={() => setActiveEngine("legal")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-[12px] font-mono font-semibold uppercase tracking-[0.14em] transition-all duration-200 ${activeEngine === "legal" ? "bg-[#818CF8]/15 text-[#818CF8] border border-[#818CF8]/30" : "text-white/35 hover:text-white/60"}`}
              >
                <Scale size={13} /> Motor Legal
              </button>
              <button
                onClick={() => setActiveEngine("verify")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-[12px] font-mono font-semibold uppercase tracking-[0.14em] transition-all duration-200 ${activeEngine === "verify" ? "bg-[#DEFF9A]/10 text-[#DEFF9A] border border-[#DEFF9A]/25" : "text-white/35 hover:text-white/60"}`}
              >
                <BriefcaseBusiness size={13} /> Motor Verify
              </button>
            </div>
          </div>

          {/* LEGAL ENGINE DETAIL */}
          {activeEngine === "legal" && (
            <div className="max-w-5xl mx-auto">
              <div className="grid lg:grid-cols-[1fr_340px] gap-6">
                <div className="space-y-5">
                  {/* Header */}
                  <div className="rounded-2xl border border-[#818CF8]/25 bg-[#818CF8]/[0.04] p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#818CF8]/15 border border-[#818CF8]/25 flex items-center justify-center">
                        <Scale size={18} className="text-[#818CF8]" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#818CF8] mb-0.5">Motor Legal</div>
                        <div className="font-display text-xl font-bold text-white">Área Legal — Peritaje Judicial</div>
                      </div>
                    </div>
                    <p className="text-[13px] text-white/50 leading-relaxed">
                      Diseñado para abogados, peritos informáticos y juzgados. El veredicto se
                      construye <strong className="text-white/80">exclusivamente sobre señales técnicas reproducibles y auditables</strong>.
                      Un perito puede replicar cada análisis con las mismas herramientas y obtener el mismo resultado meses después — requisito fundamental en un juicio.
                    </p>
                  </div>

                  {/* Signals used */}
                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle2 size={14} className="text-[#DEFF9A]" />
                      <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Señales que influyen en el veredicto</span>
                    </div>
                    <div className="space-y-2">
                      {LEGAL_SIGNALS.map(({ label, weight, note }) => (
                        <div key={label} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                          <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md border shrink-0 mt-0.5"
                            style={{ color: typeColor[weight] || "#fff", borderColor: (typeColor[weight] || "#fff") + "40", background: (typeColor[weight] || "#fff") + "12" }}>
                            {weight}
                          </span>
                          <div>
                            <div className="text-[12px] font-semibold text-white">{label}</div>
                            <div className="text-[11px] text-white/35 mt-0.5">{note}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Excluded */}
                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle size={14} className="text-orange-400" />
                      <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Solo explicativas — no afectan al score</span>
                    </div>
                    <div className="space-y-3">
                      {LEGAL_EXCLUDED.map(({ label, reason }) => (
                        <div key={label} className="p-3 rounded-lg border border-orange-500/15 bg-orange-500/[0.04]">
                          <div className="text-[12px] font-semibold text-orange-300 mb-1">{label}</div>
                          <div className="text-[11px] text-white/35 leading-relaxed">{reason}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-white/30 leading-relaxed mt-4 pt-3 border-t border-white/[0.06]">
                      Las APIs de IA ejecutan igualmente y sus observaciones aparecen en el informe como <em>contexto pericial</em>, pero el árbol de decisión solo usa señales deterministas.
                    </p>
                  </div>

                  {/* Decision tree */}
                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-4">Árbol de decisión</div>
                    <div className="space-y-2 text-[12px]">
                      {[
                        { cond: "Evidencia dura ≥ 2", verdict: "Manipulado", color: "#FF7A7A" },
                        { cond: "Evidencia dura = 1", verdict: "Sospechoso", color: "#F4D03F" },
                        { cond: "Score técnico ≥ 4.0", verdict: "Manipulado", color: "#FF7A7A" },
                        { cond: "Score técnico ≥ 2.0", verdict: "Sospechoso", color: "#F4D03F" },
                        { cond: "Sin evidencia", verdict: "Seguro", color: "#8BF56C" },
                      ].map(({ cond, verdict, color }) => (
                        <div key={cond} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                          <span className="text-white/50">{cond}</span>
                          <span className="font-mono font-bold text-[11px] px-2 py-0.5 rounded" style={{ color, background: color + "18" }}>{verdict}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VERIFY ENGINE DETAIL */}
          {activeEngine === "verify" && (
            <div className="max-w-5xl mx-auto">
              <div className="grid lg:grid-cols-[1fr_320px] gap-6">
                <div className="space-y-5">
                  <div className="rounded-2xl border border-[#DEFF9A]/20 bg-[#DEFF9A]/[0.03] p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#DEFF9A]/10 border border-[#DEFF9A]/20 flex items-center justify-center">
                        <BriefcaseBusiness size={18} className="text-[#DEFF9A]" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#DEFF9A] mb-0.5">Motor Verify</div>
                        <div className="font-display text-xl font-bold text-white">Área Verify — Verificación Profesional</div>
                      </div>
                    </div>
                    <p className="text-[13px] text-white/50 leading-relaxed">
                      Para medios de comunicación, compañías de seguros, RR.HH. y empresas que verifican evidencia digital a diario.
                      Activa la batería completa de señales incluyendo IA externa (Gemini + Sightengine) para la detección más rápida y precisa posible.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Workflow size={14} className="text-[#DEFF9A]" />
                      <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Batería completa de señales</span>
                    </div>
                    <div className="space-y-2">
                      {VERIFY_SIGNALS.map(({ label, type, note }) => (
                        <div key={label} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                          <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md border shrink-0 mt-0.5"
                            style={{ color: typeColor[type] || "#fff", borderColor: (typeColor[type] || "#fff") + "40", background: (typeColor[type] || "#fff") + "12" }}>
                            {type}
                          </span>
                          <div>
                            <div className="text-[12px] font-semibold text-white">{label}</div>
                            <div className="text-[11px] text-white/35 mt-0.5">{note}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-4">Sectores</div>
                    <div className="grid grid-cols-2 gap-2">
                      {["Prensa", "Seguros", "RR.HH.", "Marketplace", "OSINT", "Empresa"].map(s => (
                        <div key={s} className="px-3 py-2 rounded-lg border border-[#DEFF9A]/15 bg-[#DEFF9A]/[0.04] text-[11px] font-mono text-[#DEFF9A]/70 text-center">
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-4">Árbol de decisión</div>
                    <div className="space-y-2 text-[12px]">
                      {[
                        { cond: "2+ APIs IA ≥ 80%", verdict: "IA generada", color: "#818CF8" },
                        { cond: "Deepfake + señal técnica", verdict: "Manipulado", color: "#FF7A7A" },
                        { cond: "Evidencia dura ≥ 2", verdict: "Manipulado", color: "#FF7A7A" },
                        { cond: "IA confirma + técnico", verdict: "Sospechoso", color: "#F4D03F" },
                        { cond: "Sin evidencia", verdict: "Seguro", color: "#8BF56C" },
                      ].map(({ cond, verdict, color }) => (
                        <div key={cond} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                          <span className="text-white/50">{cond}</span>
                          <span className="font-mono font-bold text-[11px] px-2 py-0.5 rounded" style={{ color, background: color + "18" }}>{verdict}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── ENGINE CARDS ── */}
      <section className="relative z-10 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center mb-12">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/25 mb-3">Dos motores especializados</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Elige tu perfil forense</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {[
              {
                tag: "Motor Legal", name: "Área Legal", color: "#818CF8",
                glow: "rgba(129,140,248,0.15)", border: "rgba(129,140,248,0.22)",
                icon: Scale, to: "/register",
                desc: "Para abogados y peritos informáticos. Veredicto basado 100% en señales técnicas reproducibles. Informe PDF con cadena de custodia, hash triple y sellado TSA RFC 3161.",
                bullets: ["Señales deterministas y auditables", "ORB Copy-Move + Inpainting IA", "LSB Stego · Alineación JPEG · Solar GPS", "Gemini/Sightengine: solo explicativas", "Informe pericial válido en juicio"],
              },
              {
                tag: "Motor Verify", name: "Área Verify", color: "#DEFF9A",
                glow: "rgba(222,255,154,0.12)", border: "rgba(222,255,154,0.22)",
                icon: BriefcaseBusiness, to: "/register",
                desc: "Para medios, seguros y empresas. Batería completa con IA (Gemini + Sightengine), deepfake facial, GPS coherence, ORB Copy-Move e Inpainting. El resultado más rápido y completo.",
                bullets: ["Gemini Vision + Sightengine deepfake", "ORB Copy-Move rotacional e Inpainting", "LSB Stego · Verificación solar GPS", "Score de riesgo multi-señal 17 módulos", "Descarga PDF inmediata"],
              },
            ].map(({ tag, name, color, glow, border, icon: Icon, desc, bullets, to }) => (
              <Link key={name} to={to}
                className="group relative rounded-2xl border p-7 flex flex-col gap-5 transition-all duration-300 hover:scale-[1.015] overflow-hidden"
                style={{ borderColor: border, background: "linear-gradient(145deg, rgba(8,8,10,0.97), rgba(4,4,6,0.99))", boxShadow: `0 0 0 1px ${border}, 0 24px 70px rgba(0,0,0,0.6)` }}>
                <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-[90px] opacity-50 pointer-events-none transition-opacity duration-300 group-hover:opacity-80" style={{ background: glow }} />
                <div className="relative flex items-start justify-between">
                  <div>
                    <span className="inline-block text-[10px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border mb-3"
                      style={{ color, borderColor: border, background: glow }}>
                      {tag}
                    </span>
                    <h3 className="font-display text-2xl tracking-tight" style={{ color }}>{name}</h3>
                  </div>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: border, background: glow }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                </div>
                <p className="relative text-sm text-white/45 leading-relaxed">{desc}</p>
                <ul className="relative space-y-2">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-[12px] text-white/60">
                      <ChevronRight size={12} style={{ color }} className="shrink-0" />{b}
                    </li>
                  ))}
                </ul>
                <div className="relative flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] transition-colors mt-auto pt-2" style={{ color }}>
                  Acceder al motor <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section className="relative z-10 border-t border-white/[0.06] bg-white/[0.012]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center mb-14">
            <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/25 mb-3">Técnicas forenses integradas</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">17 módulos forenses activos</h2>
            <p className="text-white/35 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
              Desde huellas criptográficas hasta verificación solar GPS y detección de esteganografía LSB. Todos los módulos se ejecutan en cada análisis.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-5 hover:border-white/[0.14] hover:bg-white/[0.04] transition-all duration-200 group">
                <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center mb-4 group-hover:border-[#DEFF9A]/30 transition-colors">
                  <Icon size={16} className="text-white/50 group-hover:text-[#DEFF9A] transition-colors" />
                </div>
                <div className="text-[12px] font-semibold text-white mb-1.5 leading-snug">{label}</div>
                <div className="text-[11px] text-white/30 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-28 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full border border-white/[0.1] bg-white/[0.04] text-[10px] font-mono uppercase tracking-[0.22em] text-white/50">
            <Shield size={11} /> Acceso gratuito · Sin tarjeta · 3 análisis incluidos
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-5">
            Empieza tu primer análisis forense ahora
          </h2>
          <p className="text-white/40 text-sm leading-relaxed mb-8">
            Motor Legal para peritaje judicial · Motor Verify para verificación profesional diaria
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register" className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-white hover:bg-white/90 text-black font-semibold rounded-lg transition-all duration-200 shadow-[0_0_28px_rgba(255,255,255,0.1)]">
              Crear cuenta gratuita <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 border border-white/15 hover:border-white/30 text-white/60 hover:text-white rounded-lg transition-all duration-200 text-sm">
              Ya tengo cuenta
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t border-white/[0.06] py-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <ScanEyeLogoPrueba size="sm" />
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/20">
            © 2025 ScanEye Labs · Motor forense v4.0 · 17 módulos · Solar GPS · ENF · LSB Stego
          </p>
          <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.18em] text-white/25">
            <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            <Link to="/register" className="hover:text-white transition-colors">Registro</Link>
            <Link to="/verify" className="hover:text-white transition-colors">Verificar hash</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
